import React, { useEffect, useRef } from 'react';
import apiService from '../services/ApiService';
import { getViewSessionToken } from '../utils/viewSession';

interface BuildViewTrackerProps {
  vehicleId: number;
  /** e.g. "qr" from ?ref=qr */
  source?: string | null;
  /** Element to observe; defaults to document body via a sentinel rooted at this component. */
  rootMargin?: string;
  children?: React.ReactNode;
  className?: string;
  /**
   * Called with the authoritative server view_count after a successful POST
   * (whether or not this visit incremented the counter).
   */
  onViewCount?: (viewCount: number) => void;
  /** @deprecated Prefer onViewCount — still invoked when the visit is counted. */
  onCounted?: (viewCount: number) => void;
}

/**
 * Counts a public build view once per page instance when the page has been
 * in view for ≥1s. Posts to vehicle_view.php (server handles daily dedupe;
 * owner visits are not counted).
 *
 * Uses a short sentinel instead of observing the whole page: IntersectionObserver
 * ratio is visibleArea / elementArea, so a tall build page wrapping element
 * almost never reaches 50% and views would never record.
 */
export const BuildViewTracker: React.FC<BuildViewTrackerProps> = ({
  vehicleId,
  source,
  rootMargin = '0px',
  children,
  className = '',
  onViewCount,
  onCounted,
}) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const recordedRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  const onViewCountRef = useRef(onViewCount);
  const onCountedRef = useRef(onCounted);
  onViewCountRef.current = onViewCount;
  onCountedRef.current = onCounted;

  useEffect(() => {
    recordedRef.current = false;
    const el = sentinelRef.current;
    if (!el || !vehicleId) return;

    const clearTimer = () => {
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const record = () => {
      if (recordedRef.current) return;
      recordedRef.current = true;
      clearTimer();
      void apiService
        .recordVehicleView({
          vehicle_id: vehicleId,
          session_token: getViewSessionToken(),
          source: source || null,
        })
        .then((result) => {
          if (result.ok && typeof result.view_count === 'number') {
            onViewCountRef.current?.(result.view_count);
            if (result.counted) {
              onCountedRef.current?.(result.view_count);
            }
          }
        });
    };

    if (typeof IntersectionObserver === 'undefined') {
      timerRef.current = window.setTimeout(record, 1000);
      return () => clearTimer();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        // Sentinel is short; any meaningful visibility means the page is on screen.
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          if (timerRef.current == null && !recordedRef.current) {
            timerRef.current = window.setTimeout(record, 1000);
          }
        } else {
          clearTimer();
        }
      },
      { threshold: [0, 0.5, 1], rootMargin }
    );

    observer.observe(el);
    return () => {
      clearTimer();
      observer.disconnect();
    };
  }, [vehicleId, source, rootMargin]);

  return (
    <div
      className={`relative ${className}`.trim()}
      data-build-view-tracker={vehicleId}
    >
      {/* Short sentinel anchored to the page top — ratio is vs. this element, not full page height. */}
      <div
        ref={sentinelRef}
        className="absolute top-0 left-0 right-0 h-24 pointer-events-none opacity-0"
        aria-hidden
        data-build-view-sentinel
      />
      {children}
    </div>
  );
};

export default BuildViewTracker;
